import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import useFetchApi from "./useFetchApi";
import { useMutateApi } from "./useMutateApi";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLanguageEndpoint } from "@/api/config/apiEndpoints";

interface IUsePageData {
    myCustomPath?: string;
    options?: {
        listParentChildren?: boolean;
        listChildrenAddons?: boolean;
        getFormList?: boolean;
        childrenAddonsLimit?: number; // Limit for children addons (e.g., amenities)
    }
}

const usePageData = ({
    myCustomPath,
    options
}: IUsePageData = {}) => {
    const location = useLocation();
    const { language } = useLanguage();

    // Get the base path
    const basePath = myCustomPath || location.pathname;

    // Transform the path based on the selected language
    const path = getLanguageEndpoint(basePath, language);

    const {
        data,
        isLoading
    } = useFetchApi({
        url: "/v1/page" + path + "?response=json&cms_parent_page=true&cms_parent_children=" + (options?.listParentChildren ? "true" : "false") + "&cms_children_addons=" + (options?.listChildrenAddons ? "true" : "false") + (options?.getFormList ? "&cms_list_page_forms=true" : "") + (options?.childrenAddonsLimit ? `&limit=${options.childrenAddonsLimit}` : ""),
        service: "CMS_PROC"
    });

    // Normalize the data keys - remove '-Eng' suffix for English responses
    // so components can use the same key names (e.g., 'Anasayfa') regardless of language
    const normalizedData = React.useMemo(() => {
        if (!data?.data) return data?.data;

        // For Turkish, return as-is
        if (language !== 'EN') {
            return data?.data;
        }

        // For English, normalize all keys
        const normalized: any = {};
        Object.keys(data.data).forEach(key => {
            // Always keep the original key
            normalized[key] = data.data[key];

            // If key ends with '-Eng', also create a version without the suffix
            if (key.endsWith('-Eng')) {
                const baseKey = key.replace('-Eng', '');
                normalized[baseKey] = data.data[key];
            }
        });

        return normalized;
    }, [data?.data, language, path]);

    return {
        data: normalizedData,
        page: data?.page,
        localization: data?.localization,
        parentPage: data?.parentPage,
        formList: data?.formList,
        isLoading
    }

}

export default usePageData;